import dayjs from "dayjs";
import "dayjs/locale/ar";

import { getLocalizedPersonName } from "../../../../utils/companyPortal/eventApi";
import {
  apiStatusToJoiningRequestStatus,
  joiningRequestApiStatuses,
  joiningRequestStatuses,
} from "./constants";

const MIN_SEARCH_LENGTH = 1;

const readAttr = (node, key) => node?.[key] ?? node?.attributes?.[key];

const readUserId = (source = {}, user = {}) =>
  readAttr(user, "id") ?? source.user_id ?? readAttr(source, "user_id");

const readOptionName = (node = {}, lang = "en") =>
  (lang === "ar" ? readAttr(node, "name_ar") : readAttr(node, "name_en")) ||
  readAttr(node, "name") ||
  readAttr(node, "shift_type") ||
  "";

const readEventRoleRole = (role = {}) => readAttr(role, "role") || {};

const readEventRoleRoleId = (role = {}) =>
  readAttr(role, "role_id") ??
  readAttr(readEventRoleRole(role), "id") ??
  readAttr(role, "role")?.attributes?.id;

const readEventRoleName = (role = {}, lang = "en") =>
  readOptionName(readEventRoleRole(role), lang) ||
  readAttr(role, "role_name") ||
  readOptionName(role, lang);

const formatShiftName = (shift = {}, lang = "en", sharedLabels = {}) => {
  const shiftType = readAttr(shift, "shift_type");
  if (shiftType) {
    return sharedLabels?.shiftTypes?.[shiftType] || shiftType.replace(/_/g, " ");
  }

  const explicitName = readOptionName(shift, lang);
  if (explicitName) return explicitName.replace(/_/g, " ");
  const startsAt = readAttr(shift, "starts_at");
  const endsAt = readAttr(shift, "ends_at");
  if (!startsAt || !endsAt) return "";
  const start = dayjs(startsAt);
  const end = dayjs(endsAt);
  if (!start.isValid() || !end.isValid()) return "";
  return `${start.format("HH:mm")} - ${end.format("HH:mm")}`;
};

const formatRequestDate = (value, lang = "en") => {
  const parsed = dayjs(value);
  return parsed.isValid()
    ? parsed.locale(lang === "ar" ? "ar" : "en").format("DD MMM YYYY")
    : "";
};

const readRating = (attrs = {}, user = {}) => {
  const value =
    user.rating ??
    user.average_rating ??
    user.profile_rating ??
    attrs.rating ??
    attrs.user_rating ??
    attrs.average_rating ??
    0;
  const numberValue = Number(value);

  return Number.isFinite(numberValue) ? numberValue : 0;
};

const EVENT_REQUEST_JSON_API_META = new Set([
  "type",
  "id",
  "attributes",
  "relationships",
  "links",
  "meta",
]);

const readEventRequestSource = (request = {}) => {
  const attributes = request?.attributes;

  if (!attributes || typeof attributes !== "object") {
    return request;
  }

  const source = { ...attributes, id: attributes.id ?? request.id };

  Object.keys(request).forEach((key) => {
    if (EVENT_REQUEST_JSON_API_META.has(key)) return;
    if (source[key] === undefined && request[key] != null) {
      source[key] = request[key];
    }
  });

  return source;
};

export const getEventRequestListMeta = (response) => ({
  currentPage: Number(response?.headers?.["current-page"] || 1),
  count: Number(
    response?.headers?.["total-count"] ??
      response?.data?.meta?.total_count ??
      response?.data?.meta?.count ??
      0,
  ),
  pages: Number(response?.headers?.["total-pages"] || 1),
});

export const readEventCompanyId = (event = {}) =>
  event?.company_id ??
  event?.company?.id ??
  readAttr(event, "company_id") ??
  readAttr(event?.company, "id");

export const extractLoyalApplicantCountsFromResponse = (response) => {
  const data = response?.data ?? {};
  const meta = data.meta ?? {};

  const loyalRaw =
    data.loyal_applicants_count ??
    meta.loyal_applicants_count ??
    response?.headers?.["loyal-applicants-count"];
  const nonLoyalRaw =
    data.non_loyal_applicants_count ??
    meta.non_loyal_applicants_count ??
    response?.headers?.["non-loyal-applicants-count"];

  return {
    loyalApplicantsCount: Number(loyalRaw ?? 0),
    nonLoyalApplicantsCount: Number(nonLoyalRaw ?? 0),
    hasExplicitCounts: loyalRaw != null || nonLoyalRaw != null,
  };
};

export const readEventRequestCountFromResponse = (response) =>
  Number(
    response?.headers?.["total-count"] ??
      response?.data?.meta?.total_count ??
      response?.data?.meta?.count ??
      0,
  );

/**
 * Resolve a localized tag name from a `user_tag`, tolerating both shapes:
 *  - nested: { tag: { name_en, name_ar, name } }
 *  - flat:   { name_en, name_ar, name }
 */
export const readUserTagName = (userTag, lang = "en") => {
  const tag = userTag?.tag || userTag;
  if (!tag) return "";
  return String(
    (lang === "ar" ? tag.name_ar : tag.name_en) ?? tag.name ?? "",
  ).trim();
};

export const normalizeEventRequest = (request = {}, lang = "en", sharedLabels = {}) => {
  const source = readEventRequestSource(request);
  const user = source.user || {};
  const role = source.role || {};
  const shift = source.event_role_shift || source.shift || {};
  const status = apiStatusToJoiningRequestStatus[source.status] || source.status;
  const roleName = readOptionName(role, lang);
  const roleNameEn = readOptionName(role, "en");

  return {
    id: String(source.id ?? request.id ?? ""),
    requestId: source.id ?? request.id,
    userId: readUserId(source, user),
    status,
    image: user.image || user.image_url || "",
    name: getLocalizedPersonName(user, lang),
    gender: user.gender || "",
    nationalityId: user.nationality?.id,
    nationality:
      readOptionName(user.nationality, lang) ||
      user.nationality_name ||
      readOptionName(user.country, lang) ||
      "",
    zone: readOptionName(source.zone, lang) || source.zone_name || "",
    zoneId: source.zone?.id ?? source.zone_id,
    requestedRole: roleName,
    requestedRoleId: role.id ?? source.role_id,
    isSupervisor:
      String(roleNameEn || roleName || "")
        .trim()
        .toLowerCase() === "supervisor",
    requestDate: formatRequestDate(source.request_date || source.created_at, lang),
    shift: formatShiftName(shift, lang, sharedLabels),
    eventRoleShiftId: shift.id ?? source.event_role_shift_id,
    workingHours: user.total_working_hours || "",
    rating: readRating(source, user),
    is_user_checkedin: Boolean(source.is_user_checkedin),
    userTag: source.user_tag || user.user_tag || null,
    tagName: readUserTagName(source.user_tag || user.user_tag, lang),
    rejectionReason: source.rejection_reason || "",
    removalReason: source.removal_reason || "",
    removedDate: formatRequestDate(
      source.removed_at || source.removed_date,
      lang,
    ),
    raw: source,
  };
};

export const normalizeEventRequests = (items = [], lang = "en", sharedLabels = {}) =>
  (Array.isArray(items) ? items : []).map((item) =>
    normalizeEventRequest(item, lang, sharedLabels),
  );

export const readEventCheckedInSummary = (event = {}) => ({
  checkedIn: Number(readAttr(event, "number_of_checked_in_users") ?? 0),
  notCheckedIn: Number(readAttr(event, "number_of_non_checked_in_users") ?? 0),
});

export const extractCheckedInSummaryFromResponse = (response, items = []) => {
  const data = response?.data ?? {};
  const meta = data.meta ?? {};

  const checkedInRaw =
    data.number_of_checked_in_users ??
    meta.number_of_checked_in_users ??
    response?.headers?.["number-of-checked-in-users"];
  const notCheckedInRaw =
    data.number_of_non_checked_in_users ??
    meta.number_of_non_checked_in_users ??
    response?.headers?.["number-of-non-checked-in-users"];

  if (checkedInRaw != null || notCheckedInRaw != null) {
    return {
      checkedIn: Number(checkedInRaw ?? 0),
      notCheckedIn: Number(notCheckedInRaw ?? 0),
      hasExplicitCounts: true,
    };
  }

  const totalCount = getEventRequestListMeta(response).count;
  if (totalCount === 0) {
    return { checkedIn: 0, notCheckedIn: 0, hasExplicitCounts: true };
  }

  if (items.length >= totalCount) {
    let checkedIn = 0;
    let notCheckedIn = 0;

    items.forEach((item) => {
      if (item.is_user_checkedin) {
        checkedIn += 1;
      } else {
        notCheckedIn += 1;
      }
    });

    return { checkedIn, notCheckedIn, hasExplicitCounts: true };
  }

  return { checkedIn: null, notCheckedIn: null, hasExplicitCounts: false };
};

export const summaryMatchesEvent = (summary = {}, event = {}) =>
  event?.id != null && String(summary.eventId) === String(event.id);

export const resolveCheckedInSummary = (summary = {}, event = {}) => {
  if (
    summaryMatchesEvent(summary, event) &&
    summary.checkedIn != null &&
    summary.notCheckedIn != null
  ) {
    return {
      checkedIn: Number(summary.checkedIn),
      notCheckedIn: Number(summary.notCheckedIn),
    };
  }

  return readEventCheckedInSummary(event);
};

const mapAllocationStatusFilterToApiValue = (allocationFilter) => {
  const value = allocationFilter?.value;

  if (value === "assigned") return true;
  if (value === "not_assigned") return false;

  return undefined;
};

const mapCheckInFilterToApiValue = (checkInFilter) => {
  const value = checkInFilter?.value;

  if (value === "checked_in" || value === true) return true;
  if (value === "not_checked_in" || value === false) return false;

  return undefined;
};

export const buildEventRequestSearchParams = (
  search,
  { textParamKey = "user_name" } = {},
) => {
  const query = String(search || "").trim();
  if (query.length < MIN_SEARCH_LENGTH) return {};

  if (/^\d+$/.test(query)) {
    return { user_id: query };
  }

  const digitsOnly = query.replace(/\D/g, "");
  if (
    digitsOnly.length >= MIN_SEARCH_LENGTH &&
    /\d/.test(query) &&
    /\D/.test(query)
  ) {
    return { user_id: digitsOnly };
  }

  return { [textParamKey]: query };
};

const isApplicantsFilterValueActive = (value) => {
  if (value == null || value === "") return false;
  if (typeof value === "object") {
    return Object.values(value).some(
      (entry) => entry != null && entry !== "",
    );
  }
  return true;
};

export const hasActiveApplicantsSearch = (search) =>
  String(search || "").trim().length >= MIN_SEARCH_LENGTH;

export const hasActiveApplicantsFilters = (filters = {}) =>
  Object.values(filters).some(isApplicantsFilterValueActive);

const EVENT_REQUEST_TAB_COUNT_BASE_KEYS = new Set([
  "event_id",
  "status",
  "page",
  "items",
  "company_id",
  "order_by_created_at",
]);

/** True when list params have no search/filters — safe to use meta.count for tab badges. */
export const isEventRequestTabCountParams = (params = {}) => {
  if (!params?.event_id || !params?.status) return false;

  return Object.keys(params).every((key) =>
    EVENT_REQUEST_TAB_COUNT_BASE_KEYS.has(key),
  );
};

export const buildEventRequestListParams = ({
  eventId,
  activeStatus,
  search,
  filters,
  page,
  pageSize,
  companyId,
}) => {
  const ratingValue =
    filters?.rating?.value ?? filters?.rating?.min ?? filters?.rating?.id;
  const ratingMax =
    filters?.rating?.max ?? (ratingValue ? Number(ratingValue) + 0.99 : undefined);

  const params = {
    event_id: eventId,
    status: joiningRequestApiStatuses[activeStatus] || activeStatus,
    page,
    items: pageSize,
    ...buildEventRequestSearchParams(search),
    role_id: filters?.requestedRole?.id,
    event_role_shift_type: filters?.shift?.id,
    nationality_id: filters?.nationality?.id,
    gender: filters?.gender?.value,
    city_id: filters?.city?.id,
    min_total_working_hours: filters?.workingHours?.min,
    max_total_working_hours: filters?.workingHours?.max,
    // The Removed tab's date filter is a removal-date range (`removed_at`), not the
    // request-creation date — the BE exposes it via a separate param pair (PROLINE-3732).
    ...(activeStatus === joiningRequestStatuses.removed
      ? {
          removed_at_start_date: filters?.requestDate?.startDate,
          removed_at_end_date: filters?.requestDate?.endDate,
        }
      : activeStatus !== joiningRequestStatuses.joined
        ? {
            created_at_start_date: filters?.requestDate?.startDate,
            created_at_end_date: filters?.requestDate?.endDate,
          }
        : {}),
    ...(activeStatus === joiningRequestStatuses.joined && filters?.zone?.id
      ? { zone_id: filters.zone.id }
      : {}),
    ...(activeStatus === joiningRequestStatuses.joined
      ? {
          is_checked_in: mapCheckInFilterToApiValue(filters?.checkIn),
          is_assigned_to_zone: mapAllocationStatusFilterToApiValue(
            filters?.allocationStatus,
          ),
        }
      : {}),
    min_user_rating: ratingValue,
    max_user_rating: ratingMax,
    ...(filters?.applicantType?.value === "loyal" && companyId
      ? { loyal_applicants: companyId }
      : {}),
    ...(filters?.applicantType?.value === "other" && companyId
      ? { non_loyal_applicants: companyId }
      : {}),
  };

  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null),
  );
};

export const normalizeRoleLookupOptions = (rows = [], lang = "en") =>
  (Array.isArray(rows) ? rows : [])
    .map((row) => {
      if (!row || typeof row !== "object") return null;

      const id = readAttr(row, "id");
      const name =
        String(
          (lang === "ar" ? readAttr(row, "name_ar") : readAttr(row, "name_en")) ??
            readAttr(row, "name") ??
            readAttr(row, "label") ??
            "",
        ).trim() || String(id ?? "");

      return id != null && name ? { id, name } : null;
    })
    .filter(Boolean);

export const getRoleOptionsFromEvent = (event = {}, lang = "en") => {
  const roles = event?.event_roles ?? event?.event_roles_attributes ?? [];
  return (Array.isArray(roles) ? roles : [])
    .map((role) => {
      const id = readEventRoleRoleId(role);
      const name = readEventRoleName(role, lang);
      return id && name ? { id, name } : null;
    })
    .filter(Boolean);
};

export const getZoneOptionsFromEvent = (event = {}, lang = "en") => {
  const zones = event?.zones ?? event?.zones_attributes ?? [];
  return (Array.isArray(zones) ? zones : [])
    .map((zone) => {
      const id = readAttr(zone, "id");
      const name = readOptionName(zone, lang);
      return id && name ? { id, name } : null;
    })
    .filter(Boolean);
};

export const getRatingFilterOptions = () =>
  [1, 2, 3, 4, 5].map((rating) => ({
    id: rating,
    value: rating,
    min: rating,
    max: rating + 0.99,
    name: String(rating),
  }));

export const getShiftOptionsFromEvent = (
  event = {},
  lang = "en",
  sharedLabels = {},
) => {
  const roles = event?.event_roles ?? event?.event_roles_attributes ?? [];
  return (Array.isArray(roles) ? roles : []).flatMap((role) => {
    const roleId = readEventRoleRoleId(role);
    const shifts =
      readAttr(role, "event_role_shifts") ??
      readAttr(role, "event_role_shifts_attributes") ??
      [];

    return (Array.isArray(shifts) ? shifts : [])
      .map((shift) => {
        const id = readAttr(shift, "id");
        const name = formatShiftName(shift, lang, sharedLabels);
        return id && name ? { id, name, roleId } : null;
      })
      .filter(Boolean);
  });
};
